import "../../aws-amplify.config";

import { useAuth, useGroups, useUsers } from "@hooks/api";
import useViewPort from "@hooks/useViewPort";
import * as Sentry from "@sentry/nextjs";
import {
  Accordion,
  Button,
  Combobox,
  Dropdown,
  ErrorMessage,
  Footer,
  Header,
  InfoMessage,
  Spinner,
  Text
} from "@taskalliance/lms";
import { useAlert } from "@utils/alertMessage";
import { AlertType } from "@utils/alertUtils";
import { settingsQuery } from "@utils/groqQueries";
import { isValidEmail, isValidUserName } from "@utils/validCheck";
import EyeOff from "assets/images/icon_EyeOff.svg";
import EyeOn from "assets/images/icon_EyeOn.svg";
import { Auth } from "aws-amplify";
import { getClient } from "libs/sanity-library/src/lib/sanity.server";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { groq } from "next-sanity";
import {
  FunctionComponent,
  useEffect,
  useReducer,
  useRef,
  useState
} from "react";
import { ParsePageLanguage } from "@taskalliance/sanity-library/Languages";
import styles from "./signup.module.scss";
import { PortableText } from "@portabletext/react";

const signUpPageQuery = groq`
  *[_type in ["signUpPage"]] {
    ...
  }
`;

const formReducer = (state: any, event: any) => {
  return {
    ...state,
    [event.name]: event.value
  };
};

function sortByTitle(a, b) {
  const nameA = a.title.toUpperCase();
  const nameB = b.title.toUpperCase();
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  return 0;
}

interface SignUpProps {
  data: {
    sanityData: {
      pageMainTitle: string;
      nameLabel: string;
      emailLabel: string;
      repeatEmailLabel: string;
      passwordLabel: string;
      passwordRequirements: string;
      federationsLabel: string;
      roleLabel: string;
      topAthleteLabel: string;
      eliteAthlete: string;
      talentAthlete: string;
      coach: string;
      defaultOptionDropdown: string;
      consentCheckboxTitle: string;
      consentCheckboxBodyPb: any;
      verifyCode: string;
      infoTextVerificationCode: string;
      codeLabel: string;
      resendCodeText: string;
      codeIsResentTxt: string;
      doneRegistered: string;
      doneRegisteredInfo: string;
      buttons: {
        sendButton: string;
        nextButton: string;
        resendButton: string;
        cancelButton: string;
        continueButton: string;
        ariaButtonHidePassword: string;
        ariaButtonShowPassword: string;
      };
      texts: {
        errorInvalidUserName: string;
        errorInvalidEmail: string;
        errorEmailsDoNotMatch: string;
        errorEmailAlreadyExist: string;
        errorYouAlreadyExist: string;
        signInOrtryAgainWithAnotherEmail: string;
        registeredBefore: string;
        errorWrongCode: string;
        errorCodeMismatchException: string;
        errorNewPasswordDoesNotMeetRequirements: string;
        errorLimitExceededException: string;
        errorSomethingWentWrong: string;
      };
    };
    setting: any;
  };
}

enum PageStatus {
  START = "start",
  VERIFY = "verify",
  COMPLETED = "completed"
}

const SignUp: FunctionComponent<SignUpProps> = ({ data }) => {
  //filter language data using router
  const router = useRouter();
  data = ParsePageLanguage(data, router.locale);

  const [groups, setGroups] = useState<any>([]);
  const [federations, setFederations] = useState<any>([]);
  const [showIcon, setShowIcon] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasInfoMessage, setHasInfoMessage] = useState<boolean>(false);
  const [hasAlertMessage, setHasAlertMessage] = useState<boolean>(false);
  const [pageStatus, setPageStatus] = useState<PageStatus>(PageStatus.START);

  const [profileUpdateAttempts, setProfileUpdateAttempts] = useState(0);
  const maxAttempts = 5; // Maximum number of attempts to update profile
  const attemptDelay = 1000; // Delay between attempts in milliseconds

  const eyeOnRef = useRef(null);
  const eyeOffRef = useRef(null);
  const passwordRef = useRef(null);
  const passwordToggleRef = useRef(null);

  const { viewportWidth } = useViewPort();
  const { getGroupsWithClientId } = useGroups();
  const { updateUser, addUserToGroups } = useUsers();
  const { loginjwt, logout, signUp, userId } = useAuth();
  const [alertMessage, setAlert, clearAlert] = useAlert();
  const [formData, setFormData] = useReducer(formReducer, {
    username: undefined,
    email: undefined,
    repeatEmail: undefined,
    password: undefined,
    federation: undefined,
    federationChild: undefined,
    role: undefined,
    consent: false,
    verificationCode: undefined
  });

  const typeOfRole = [
    { title: data.sanityData.eliteAthlete, id: data.sanityData.eliteAthlete },
    { title: data.sanityData.talentAthlete, id: data.sanityData.talentAthlete },
    { title: data.sanityData.coach, id: data.sanityData.coach }
  ];

  useEffect(() => {
    const f = async () => {
      const groups = await getGroupsWithClientId();
      setGroups(groups);
    };
    f();
  }, []);

  useEffect(() => {
    const f = async () => {
      const tmpgroup = groups.groups?.map((group) => {
        group.title = group.meta.title[router.locale];
        return group;
      });
      const fedChildren = tmpgroup.filter(
        (fed) => fed.meta.role === "federation-child"
      );
      const fedParent = tmpgroup.filter(
        (fed) => fed.meta.role === "federation"
      );

      const mergedAndFiltered = fedChildren.map((child) => {
        const parent = fedParent.find(
          (parent) => parent.id === child.parentGroupId
        );

        return {
          id: child.id,
          meta: child.meta,
          parentGroupId: child.parentGroupId,
          parentTitle: parent ? parent.title : null,
          title: parent ? `${child.title}, ${parent.title}` : child.title
        };
      });

      setFederations(mergedAndFiltered?.sort(sortByTitle));
    };
    if (groups?.groups?.length > 0) f();
  }, [router.locale, groups]);

  function getInputClassName(inputId: string) {
    return hasAlertMessage && alertMessage?.errorInputIds?.includes(inputId)
      ? styles.inputWithError
      : "";
  }

  useEffect(() => {
    if (alertMessage != undefined && alertMessage.alertBody !== "") {
      setHasAlertMessage(true);
    } else {
      setHasAlertMessage(false);
    }
  }, [alertMessage]);

  const toggleVisibility = (e: any) => {
    e.preventDefault();
    if (passwordRef.current.type === "password") {
      passwordRef.current.type = "text";
      setShowIcon(!showIcon);
      passwordToggleRef.current.setAttribute(
        "aria-label",
        data.sanityData.buttons.ariaButtonShowPassword
      );
    } else {
      passwordRef.current.type = "password";
      setShowIcon(!showIcon);
      passwordToggleRef.current.setAttribute(
        "aria-label",
        data.sanityData.buttons.ariaButtonHidePassword
      );
    }
  };

  const getDataFromAWS = async () => {
    // get the currentauthenticateduser in aws
    const user = await Auth.currentAuthenticatedUser();
    formData.email = user.attributes.email?.toLowerCase().trim();
  };

  const handleChange = (event) => {
    clearAlert();
    setHasInfoMessage(false);

    const isCheckbox = event.target.type === "checkbox";
    setFormData({
      name: event.target.name,
      value: isCheckbox ? event.target.checked : event.target.value
    });
  };

  const handleComboboxChange = (childId: string, parentId: string) => {
    setFormData({
      name: "federationChild",
      value: childId
    });
    setFormData({
      name: "federation",
      value: parentId
    });
  };

  const handleRoleChange = (role: string) => {
    setFormData({
      name: "role",
      value: role
    });
  };

  const sendUserProfile = async () => {
    Sentry.captureMessage(
      `sendUserProfile | userId: ${userId} | formData.username: ${formData.username} | formData.email: ${formData.email} | formData.federation: ${formData.federation} | formData.federationChild: ${formData.federationChild} | formData.role: ${formData.role}`
    );
    try {
      if (!userId) {
        Sentry.captureMessage("sendUserProfile | userId is not available");
        console.log("Waiting for userId to be available...");
        return false;
      }

      if (!formData.username || !formData.email) {
        Sentry.captureMessage(
          "sendUserProfile | username or email is not available"
        );
        console.error("Missing required form data:", { formData });
        return false;
      }

      Sentry.setUser({ email: formData.email?.trim().toLowerCase() });
      Sentry.setUser({ id: userId });

      const splitNameArray = formData.username?.trim().split(" ");
      const lastName = splitNameArray.pop();
      const firstName = splitNameArray.join(" ");

      const userData = {
        email: formData.email?.trim().toLowerCase(),
        firstName,
        lastName,
        language: router.locale,
        profileCompleted: true
      };

      await updateUser({ userId, data: userData });
      const groupIds = getUserGroups();
      Sentry.captureMessage(
        `sendUserProfile | groupIds: ${groupIds.toString()}`
      );
      await addUserToGroups({ groupIds, userId });
      return true;
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error updating user profile:", error);
      return false;
    }
  };

  /*
  useEffect(() => {
    const f = async () => {
      await sendUserProfile();

      //setPageStatus(PageStatus.COMPLETED);
    };
    if (userId) f();
  }, [userId]);
  */
  // Add an effect to handle profile updates after userId is available
  useEffect(() => {
    const attemptProfileUpdate = async () => {
      if (!userId || pageStatus !== PageStatus.VERIFY) return;

      const success = await sendUserProfile();

      if (success) {
        setPageStatus(PageStatus.COMPLETED);
        setProfileUpdateAttempts(0); // Reset attempts counter
      } else if (profileUpdateAttempts < maxAttempts) {
        // Schedule another attempt
        setTimeout(() => {
          setProfileUpdateAttempts((prev) => prev + 1);
        }, attemptDelay);
      } else {
        // Max attempts reached, show error
        Sentry.captureMessage(
          "Failed to update user profile after maximum attempts"
        );
        setAlert(
          "",
          data.sanityData.texts.errorSomethingWentWrong,
          AlertType.ERROR
        );
        setProfileUpdateAttempts(0); // Reset attempts counter
      }
    };

    if (userId) {
      attemptProfileUpdate();
    }
  }, [userId, profileUpdateAttempts, pageStatus]);

  const getUserGroups = () => {
    const groupsToAdd = [];
    if (formData.federation !== "") {
      groupsToAdd.push(formData.federation);
      groupsToAdd.push(formData.federationChild);
    }
    if (formData.role === data.sanityData.eliteAthlete) {
      const role = groups.role.find(
        (g) => g.meta.shortname === "elite-athlete"
      );
      groupsToAdd.push(role.id);
    } else if (formData.role === data.sanityData.talentAthlete) {
      const role = groups.role.find(
        (g) => g.meta.shortname === "talent-athlete"
      );
      groupsToAdd.push(role.id);
    } else if (formData.role === data.sanityData.coach) {
      const role = groups.role.find(
        (g) => g.meta.shortname === "coach-support"
      );
      groupsToAdd.push(role.id);
    }

    return groupsToAdd;
  };

  const signUpUser = async (e: any) => {
    e.preventDefault();
    Sentry.setUser({ email: formData.email?.trim().toLowerCase() });
    if (!isValidUserName(formData.username)) {
      setAlert(
        "",
        data.sanityData.texts.errorInvalidUserName,
        AlertType.ERROR,
        ["username"]
      );
      return;
    } else if (!isValidEmail(formData.email)) {
      setAlert("", data.sanityData.texts.errorInvalidEmail, AlertType.ERROR, [
        "email"
      ]);
      return;
    } else if (!isValidEmail(formData.repeatEmail)) {
      setAlert("", data.sanityData.texts.errorInvalidEmail, AlertType.ERROR, [
        "repeatEmail"
      ]);
      return;
    } else if (
      formData?.repeatEmail?.toLowerCase() !== formData?.email?.toLowerCase()
    ) {
      setAlert(
        "",
        data.sanityData.texts.errorEmailsDoNotMatch,
        AlertType.ERROR,
        ["email", "repeatEmail"]
      );
      return;
    }

    //Trying sign in first, to see if the user already exist
    try {
      setIsLoading(true);
      await Auth.signIn(
        formData.email.trim().toLowerCase(),
        formData.password?.trim()
      );

      setAlert(
        data.sanityData.texts.errorYouAlreadyExist,
        data.sanityData.texts.signInOrtryAgainWithAnotherEmail,
        AlertType.ERROR,
        ["email", "repeatEmail"]
      );
      setIsLoading(false);
      return;
    } catch (error) {
      console.log({ signinError: error });

      if (error.code === "UserNotConfirmedException") {
        resendConfirmationCode();
        setPageStatus(PageStatus.VERIFY);
        setIsLoading(false);
        return;
      } else if (
        error.code === "InvalidParameterException" ||
        error.code === "InvalidPasswordException"
      ) {
        setAlert(
          data.sanityData.texts.errorNewPasswordDoesNotMeetRequirements,
          data.sanityData.passwordRequirements,
          AlertType.ERROR,
          ["password"]
        );
        setIsLoading(false);
        return;
        //exists but wrong password
      } else if (error.code === "NotAuthorizedException") {
        setAlert("", data.sanityData.texts.registeredBefore, AlertType.ERROR, [
          "email",
          "repeatEmail"
        ]);
        setIsLoading(false);
        return;
      } else if (
        error.code === "UserNotFoundException" ||
        error.code === "NotAuthorizedException"
      ) {
        clearAlert();
      }
    }

    try {
      await signUp({
        email: formData.email?.trim().toLowerCase(),
        password: formData.password?.trim()
      });

      setPageStatus(PageStatus.VERIFY);
      setFormData({
        name: "verificationCode",
        value: ""
      });
      setIsLoading(false);
    } catch (error) {
      console.log({ signupError: error });
      setIsLoading(false);
      if (
        error.code === "InvalidParameterException" ||
        error.code === "InvalidPasswordException"
      ) {
        setAlert(
          data.sanityData.texts.errorNewPasswordDoesNotMeetRequirements,
          data.sanityData.passwordRequirements,
          AlertType.ERROR,
          ["password"]
        );
        setIsLoading(false);
      } else if (error.code === "UsernameExistsException") {
        setAlert(
          data.sanityData.texts.errorEmailAlreadyExist,
          data.sanityData.texts.errorInvalidEmail,
          AlertType.ERROR,
          ["email", "repeatEmail"]
        );
        setIsLoading(false);
        return;
      } else {
        Sentry.captureException(error);
        setIsLoading(false);
        setAlert(
          "",
          data.sanityData.texts.errorSomethingWentWrong,
          AlertType.ERROR
        );
      }
    }
  };

  const verifyCode = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Step 1: Confirm signup
      await Auth.confirmSignUp(
        formData.email?.toLowerCase().trim(),
        formData.verificationCode?.trim()
      );

      // Step 2: Sign in
      await Auth.signIn(
        formData.email?.toLowerCase().trim(),
        formData.password?.trim()
      );

      // Step 3: Get session and token
      const authSession = await Auth.currentSession();
      const idToken = await authSession.getIdToken().getJwtToken();

      // Step 4: Login with JWT
      await loginjwt(idToken);

      // The useEffect will handle the profile update once userId is available
      setProfileUpdateAttempts(0); // Reset attempts counter
    } catch (error) {
      console.log({ error });
      setIsLoading(false);

      if (error.code === "CodeMismatchException") {
        setAlert(
          data.sanityData.texts.errorWrongCode,
          data.sanityData.texts.errorCodeMismatchException,
          AlertType.ERROR,
          ["verificationCode"]
        );
      } else {
        Sentry.captureException(error);
        setAlert(
          "",
          data.sanityData.texts.errorSomethingWentWrong,
          AlertType.ERROR,
          ["verificationCode"]
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resendConfirmationCode = async () => {
    try {
      await Auth.resendSignUp(formData.email?.trim().toLowerCase());
      setHasInfoMessage(true);
    } catch (error) {
      console.log({ error });
      Sentry.captureException(error);
      setHasInfoMessage(false);
      if (error.code === "LimitExceededException") {
        setAlert(
          "",
          data.sanityData.texts.errorLimitExceededException,
          AlertType.ERROR,
          ["email"]
        );
      } else {
        setAlert(
          "",
          data.sanityData.texts.errorSomethingWentWrong,
          AlertType.ERROR,
          ["email"]
        );
      }
    }
  };

  const logOutAndGoToLogIn = async (e: any) => {
    e.preventDefault();
    await Auth.signOut({ global: true });
    await logout();
    router.push("/auth/login");
  };

  //sanity portabletext styling/parsing
  const customComponents = {
    block: {
      normal: ({ children }: any) => (
        <Text as="p" size="small" forceDangerouslySetInnerHTML={true}>
          {children}
        </Text>
      )
    }
  };

  if (pageStatus === PageStatus.START) {
    return (
      <>
        <Head>
          <title>{data.sanityData.pageMainTitle}</title>
        </Head>
        <main>
          <Header
            headerData={data.setting?.header}
            languages={true}
            hrefLanguages="/auth/signup"
          />
          <article>
            <div className={styles.signUp}>
              <Text as="h1">{data.sanityData.pageMainTitle}</Text>
              <form onSubmit={signUpUser}>
                <>
                  <div className={styles.inputwrapper}>
                    <label htmlFor="username">
                      <Text>{data.sanityData.nameLabel}</Text>
                    </label>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      onChange={handleChange}
                      autoComplete="off"
                      className={getInputClassName("username")}
                    />

                    <label htmlFor="email">
                      <Text>{data.sanityData.emailLabel}</Text>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="text"
                      onChange={handleChange}
                      className={getInputClassName("email")}
                    />

                    <label htmlFor="repeatEmail">
                      <Text>{data.sanityData.repeatEmailLabel}</Text>
                    </label>
                    <input
                      id="repeatEmail"
                      name="repeatEmail"
                      type="text"
                      onChange={handleChange}
                      className={getInputClassName("repeatEmail")}
                    />

                    <div className={styles.inputwrapper}>
                      <label htmlFor="password">
                        <Text>{data.sanityData.passwordLabel}</Text>
                      </label>
                      <input
                        ref={passwordRef}
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        spellCheck="false"
                        onChange={handleChange}
                        className={getInputClassName("password")}
                      />

                      <div className={styles.eyecons}>
                        <button
                          onClick={toggleVisibility}
                          aria-controls="password"
                          type="button"
                          aria-label={
                            data.sanityData.buttons.ariaButtonShowPassword
                          }
                          ref={passwordToggleRef}
                        >
                          <div ref={eyeOffRef}>
                            <EyeOff
                              style={{
                                display: `${!showIcon ? "block" : "none"}`
                              }}
                            />
                          </div>
                          <div ref={eyeOnRef}>
                            <EyeOn
                              style={{
                                display: `${showIcon ? "block" : "none"}`
                              }}
                            />
                          </div>
                        </button>
                      </div>
                    </div>
                    <div className={styles.passwordRequirements}>
                      <Text as="p" size="xsmall">
                        {data.sanityData.passwordRequirements}
                      </Text>
                    </div>
                    {federations && federations.length > 0 && (
                      <Combobox
                        id="federation"
                        label={data.sanityData.federationsLabel}
                        options={federations}
                        onSelect={handleComboboxChange}
                        placeholder={data.sanityData.defaultOptionDropdown}
                      />
                    )}
                    <Combobox
                      id="role"
                      label={data.sanityData.roleLabel}
                      options={typeOfRole}
                      readOnlyInput={true}
                      onChange={handleRoleChange}
                      placeholder={data.sanityData.defaultOptionDropdown}
                    />
                    <div className={styles.checkboxwrapper}>
                      <label htmlFor="">
                        <Accordion>
                          <Accordion.Item id="d">
                            <div className={styles.checkboxAndLabel}>
                              <input
                                id="consent"
                                name="consent"
                                type="checkbox"
                                onChange={handleChange}
                                required
                              />
                              <Accordion.Header contentId="j">
                                <Text>
                                  {data.sanityData.consentCheckboxTitle}
                                </Text>
                              </Accordion.Header>
                            </div>
                            <Accordion.Content id="l">
                              <PortableText
                                value={data.sanityData.consentCheckboxBodyPb}
                                components={customComponents}
                              />
                            </Accordion.Content>
                          </Accordion.Item>
                        </Accordion>
                      </label>
                    </div>
                    {hasAlertMessage && (
                      <ErrorMessage>
                        <>
                          <Text
                            as="p"
                            size="small"
                            style={{ fontWeight: "bold" }}
                          >
                            {alertMessage.alertTitle}
                          </Text>
                          <Text as="p" size="small">
                            {alertMessage.alertBody}
                          </Text>
                        </>
                      </ErrorMessage>
                    )}
                  </div>
                  <div className={styles.buttonCenter}>
                    <Button
                      type="submit"
                      variant={"primary"}
                      size={viewportWidth < 768 ? "medium" : "large"}
                      attribute="submit-form"
                      disabled={
                        !formData.username ||
                        !formData.email ||
                        !formData.repeatEmail ||
                        !formData.password ||
                        !formData.federation ||
                        !formData.role ||
                        !formData.consent ||
                        isLoading ||
                        !formData.role
                      }
                    >
                      {!isLoading ? (
                        data.sanityData.buttons.nextButton
                      ) : (
                        <Text as="span" style={{ paddingBlock: "0.7rem" }}>
                          <Spinner />
                        </Text>
                      )}
                    </Button>
                  </div>
                </>
              </form>
              <Text as="p">
                <Link href="/">{data.sanityData.buttons.cancelButton}</Link>
              </Text>
            </div>
          </article>
          <Footer footerData={data.setting?.footer} />
        </main>
      </>
    );
  }
  if (pageStatus === PageStatus.VERIFY) {
    return (
      <>
        <Head>
          <title>{data.sanityData.verifyCode}</title>
        </Head>
        <main>
          <Header headerData={data.setting?.header} />
          <article>
            <div className={styles.container}>
              <div className={styles.signUp}>
                <Text as="h1">{data.sanityData.verifyCode}</Text>
                <Text as="p" customClassName={styles.instructionTxt}>
                  {data.sanityData.infoTextVerificationCode}
                </Text>
                <form onSubmit={verifyCode}>
                  <div className={styles.inputverifywrapper}>
                    <label htmlFor="verificationCode">
                      <Text>{data.sanityData.codeLabel}</Text>
                    </label>
                    <input
                      id="verificationCode"
                      name="verificationCode"
                      type="text"
                      onChange={handleChange}
                      value={formData.verificationCode}
                      className={getInputClassName("verificationCode")}
                      autoFocus={true}
                    />
                    {hasAlertMessage &&
                      alertMessage.errorInputIds?.includes(
                        "verificationCode"
                      ) && (
                        <ErrorMessage>
                          <>
                            <Text as="p" size="medium">
                              {alertMessage.alertTitle}
                            </Text>
                            <Text as="p" size="small">
                              {alertMessage.alertBody}
                            </Text>
                          </>
                        </ErrorMessage>
                      )}
                    {hasInfoMessage && (
                      <InfoMessage>
                        <Text as="p" size="medium">
                          {data.sanityData.codeIsResentTxt}
                        </Text>
                      </InfoMessage>
                    )}
                  </div>
                  <div className={styles.buttonCenter}>
                    <Button
                      type="submit"
                      variant="secondary"
                      attribute="verify"
                      size={viewportWidth < 768 ? "xsmall" : "small"}
                      disabled={!formData.verificationCode || isLoading}
                    >
                      {!isLoading ? (
                        data.sanityData.buttons.sendButton
                      ) : (
                        <Text as="span" style={{ paddingBlock: "0.7rem" }}>
                          <Spinner />
                        </Text>
                      )}
                    </Button>
                  </div>
                </form>
                <Text as="p" customClassName={styles.instructionTxt}>
                  {data.sanityData.resendCodeText}
                </Text>
                <div className={styles.buttonCenter}>
                  <Link href="#" onClick={resendConfirmationCode}>
                    {data.sanityData.buttons.resendButton}
                  </Link>
                </div>
                <div className={styles.buttonCenter}>
                  <Link href="/">{data.sanityData.buttons.cancelButton}</Link>
                </div>
              </div>
            </div>
          </article>
          <Footer footerData={data.setting?.footer} />
        </main>
      </>
    );
  }
  if (pageStatus === PageStatus.COMPLETED) {
    return (
      <>
        <Head>
          <title>{data.sanityData.doneRegistered}</title>
        </Head>
        <main>
          <Header headerData={data.setting?.header} />
          <article>
            <div className={styles.container}>
              <div className={styles.signUp}>
                <Text as="h1">{data.sanityData.doneRegistered}</Text>
                <div className={styles.buttonCenter}>
                  <Button
                    type="submit"
                    variant={"primary"}
                    attribute="goto-login"
                    size={viewportWidth < 768 ? "medium" : "large"}
                    onClick={logOutAndGoToLogIn}
                  >
                    {data.sanityData.buttons.continueButton}
                  </Button>
                </div>
                <br />
                <Text as="p" customClassName={styles.instructionTxt}>
                  {data.sanityData.doneRegisteredInfo}
                </Text>
              </div>
            </div>
          </article>
          <Footer footerData={data.setting?.footer} />
        </main>
      </>
    );
  }
};

export async function getStaticProps({ preview = false }) {
  const sanityData = await getClient(preview).fetch(signUpPageQuery);
  const setting = await getClient(preview).fetch(settingsQuery);

  return {
    props: {
      preview,
      data: { sanityData: sanityData[0], setting: setting[0] }
    },
    revalidate: 1
  };
}

export default SignUp;
